import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { MasteryPacing } from '@/enums';
import { UserBadge } from "@/components/shared";
import { MasteryChangeChart } from "@/features/planner/components";
import { getMasteryKey } from "@/utilities";

import "./BadgeWithTooltip.scss";
import { SkilltreeIcon } from "@/features/planner/icons";

import StatusCard from "@/features/wizard/components/PrerequisiteCreateModal/StatusCard";

enum PopoverTemplate {
  PREREQ = "PREREQ",
  TIME_NEEDED = "TIME_NEEDED",
}

interface BadgeWithTooltipProps {
  showIcon?: boolean;
  fullName: string;
  firstName: string;
  displayName: string;
  errorCount: number;
  exposureCount: number;
  masteryNow: keyof typeof MasteryPacing;
  masteryMonday?: keyof typeof MasteryPacing;
  template?: keyof typeof PopoverTemplate;
}

export const PrerequisiteBox = ({
  exposureCount,
  errorCount,
  masteryNow
}: {
  exposureCount: number;
  errorCount: number;
  masteryNow: keyof typeof MasteryPacing;
}) => {
  const { t } = useTranslation("common");
  const masteryStatusClass = getMasteryKey(masteryNow);
  return(
<div className={`prerequisite-box card-mastery card-mastery-${masteryStatusClass}`}>
  <h4 className="prerequisite-header">
    {t(masteryStatusClass)}
    <span className="dot-indicator" />
  </h4>

  <div className="prerequisite-details">
    <span className="prerequisite-detail">
      {exposureCount} observations
    </span>

    <span className="prerequisite-detail">
      {errorCount} errors
    </span>
  </div>
</div>
  );
};

export const TimeNeededBox = ({
  exposureCount,
  errorCount,
  masteryNow
}: {
  exposureCount: number;
  errorCount: number;
  masteryNow: keyof typeof MasteryPacing;
}) => {
  const { t } = useTranslation("common");
  const masteryStatusClass = getMasteryKey(masteryNow);
  return(
    <div className={`time-needed-box card-mastery card-mastery-${masteryStatusClass}`}>
      <div className="time-needed-chart">
        <MasteryChangeChart />
      </div>
      <div className="time-needed-details">
        <h4 className="time-needed-header">{t(masteryStatusClass)}</h4>
        <span className="time-needed-detail">
          {exposureCount} observations
        </span>
        <span className="time-needed-detail">
          {errorCount} errors
        </span>
      </div>
    </div>
)}

export const BadgeWithTooltip = ({
  showIcon = false,
  fullName,
  firstName,
  displayName,
  errorCount,
  exposureCount,
  masteryNow,
  template = PopoverTemplate.PREREQ,
}: BadgeWithTooltipProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { t } = useTranslation("rolloverWizard");

  return (
    <div
      className="tooltip-wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="user-context">
        <UserBadge name={fullName} />
        <span className="user-label">{fullName}</span>
        <span className={`badge badge-pill badge-${masteryNow === MasteryPacing.BELOW_GRADE_LEVEL ? 'undeveloped': 'developing'}`}>
          {`${errorCount}/${exposureCount}`}
          {showIcon && <SkilltreeIcon color="currentColor" width="18" height="18" />}
        </span>
      </div>
      {showTooltip && (
        <div className={`custom-tooltip ${template === PopoverTemplate.TIME_NEEDED ? 'custom-tooltip--wide' : ''}`}>
          <div className="tooltip-title">
            {t(`${template}_MESSAGE`, { firstName, displayName, correctCount: errorCount, exposureCount }) ||
             `${firstName} is most likely not ready for this objective. The prerequisite skill "${displayName}" is not developed.`}
          </div>
          {template === PopoverTemplate.PREREQ && <>
            <span className="prerequisite-label">
              {t(`${template}_LABEL`, { firstName, displayName, correctCount: errorCount, exposureCount }) || "Prerequisite Skill Mastery"}
            </span>
            <PrerequisiteBox masteryNow={masteryNow} exposureCount={exposureCount} errorCount={errorCount} />
          </>}
          {template === PopoverTemplate.TIME_NEEDED && <StatusCard status={MasteryPacing[masteryNow]} masteryStart={MasteryPacing[masteryNow]} masteryEnd={MasteryPacing[masteryNow]} observations={exposureCount} errors={errorCount} showGraph={true} preReqModal={false}/>}
          <div className="arrow-border" />
          <div className="arrow" />
        </div>
      )}
    </div>
  );
};

export default BadgeWithTooltip;